import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { getFragmentData } from "../../__generated__/fragment-masking";
import { AddressFieldsFragmentDoc, ClientAddressesDocument } from "../../__generated__/graphql";
import { useClientId } from "../../shared";

/** Flat address shape consumed by the address list component. */
export type AddressItem = {
  id: string;
  isPrimary: boolean;
  header: string;
  body: string;
};

const SEPARATOR = "・";

/** Join non-empty address parts using the shared separator. */
function joinAddressParts(...parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(SEPARATOR);
}

export function useClientAddresses({ skip = false }: { skip?: boolean } = {}) {
  const clientId = useClientId();
  const result = useQuery(ClientAddressesDocument, {
    skip: skip || !clientId,
    fetchPolicy: "cache-first",
    variables: { filters: { clientId } },
  });

  const addresses = useMemo(() => {
    const list = result.data?.clientAddresses.addresses;
    if (!list) return [];
    const unmasked = getFragmentData(AddressFieldsFragmentDoc, list);
    // Primary-first stable sort — every consumer wants this ordering.
    return [...unmasked].sort((a, b) => Number(b.primary) - Number(a.primary));
  }, [result.data?.clientAddresses.addresses]);

  // Map raw addresses into the flat shape the address list renders. When an
  // address has a label it leads the row, with the postal code folded into the
  // supporting line; otherwise the postal code leads on its own.
  const addressLists = useMemo<AddressItem[]>(() => {
    return addresses.map((address) => {
      const hasLabel = Boolean(address.label?.trim());
      return {
        id: address.id,
        isPrimary: address.primary,
        header: hasLabel ? (address.label ?? "") : address.postalCode,
        body: hasLabel
          ? joinAddressParts(address.postalCode, address.fullAddress, address.unitNumber)
          : joinAddressParts(address.fullAddress, address.unitNumber),
      };
    });
  }, [addresses]);

  // Total addresses as reported by the server — trust this over the local list
  // length when detecting whether the address limit has been reached.
  const count = result.data?.clientAddresses.count ?? 0;

  return { ...result, addresses, addressLists, count };
}
